import React, { useEffect, useState } from "react";
import { adminApi, adminGetTonight, adminSetTonight, beverageList } from "../../lib/adminApi";
import BottleSilhouette from "../../components/BottleSilhouette";

const MOOD_OPTIONS = ["", "monsoon", "coastal", "festive", "post-work", "winter", "summer", "celebration", "quiet", "weird shelf"];
const WHY_OPTIONS = [
  "", "Monsoon eve", "Goa weather", "Bartender's obsession",
  "New arrival", "Recent discovery", "Limited stock", "Seafood pairing",
  "Festive night", "Late-shift comfort", "Quiet rainy evening",
];

export default function AdminTonight() {
  const [gins, setGins] = useState([]);
  const [flights, setFlights] = useState([]);
  const [beverages, setBeverages] = useState([]);
  const [activeTarget, setActiveTarget] = useState("gin"); // gin | beverage | flight
  const [form, setForm] = useState({
    gin_id: "",
    beverage_id: "",
    flight_id: "",
    handwritten_note: "",
    note: "",
    bartender: "Shrinkhala",
    why_tonight: "",
    mood: "",
    expires_at: "",
  });
  const [savedAt, setSavedAt] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/gins").then((r) => setGins(r.data.gins));
    adminApi.get("/admin/flights").then((r) => setFlights([...r.data.static, ...(r.data.admin || [])]));
    beverageList({ availability: "AVAILABLE", limit: 1000 }).then((d) => setBeverages(d.beverages || [])).catch(() => {});

    adminGetTonight().then((d) => {
      setActive(d.active ? d : null);
      if (d.active) {
        const target = d.beverage ? "beverage" : d.flight ? "flight" : "gin";
        setActiveTarget(target);
        setForm({
          gin_id: d.gin?.id || "",
          beverage_id: d.beverage?.id || "",
          flight_id: d.flight?.id || "",
          handwritten_note: d.handwritten_note || "",
          note: d.note || "",
          bartender: d.bartender || "Shrinkhala",
          why_tonight: d.why_tonight || "",
          mood: d.mood || "",
          expires_at: d.expires_at ? d.expires_at.slice(0, 16) : "",
        });
      }
    });
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Build payload, zeroing out non-selected targets so we never violate the mutual exclusion guard
      const payload = {
        gin_id: activeTarget === "gin" ? (form.gin_id || null) : null,
        beverage_id: activeTarget === "beverage" ? (form.beverage_id || null) : null,
        flight_id: activeTarget === "flight" ? (form.flight_id || null) : null,
        handwritten_note: form.handwritten_note || null,
        note: form.note || null,
        bartender: form.bartender || null,
        why_tonight: form.why_tonight || null,
        mood: form.mood || null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      await adminSetTonight(payload);
      setSavedAt(new Date().toLocaleTimeString());
      const d = await adminGetTonight();
      setActive(d.active ? d : null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Failed to save");
    } finally { setBusy(false); }
  };

  const clearTonight = async () => {
    if (!window.confirm("Clear tonight's pour? Public site will fall back to the daily rotation.")) return;
    try {
      await adminApi.delete("/admin/tonight");
      setActive(null);
      setForm({ ...form, gin_id: "", beverage_id: "", flight_id: "", handwritten_note: "", note: "", why_tonight: "", expires_at: "" });
    } catch { /* ignore */ }
  };

  const availableGins = gins.filter((g) => g.availability_status !== "NOT_AVAILABLE" && g.availability_status !== "HIDDEN");
  const availableBeverages = beverages.filter((b) => b.availability_status === "AVAILABLE");

  return (
    <div className="px-5 md:px-10 py-8 max-w-5xl" data-testid="admin-tonight">
      <header className="mb-6">
        <p className="overline text-[#C17F59] mb-2">Tonight at BENO · Liquid Atlas hero</p>
        <h1 className="font-display text-4xl">Pick the night's pour.</h1>
        <p className="text-sm text-white/55 mt-2 max-w-xl">
          What you set here becomes the emotional hero of the Liquid Atlas homepage —
          bartender attribution, handwritten note, optional "why tonight?" context.
          Only AVAILABLE bottles can be featured.
        </p>
      </header>

      {/* Active state */}
      {active && (
        <div className="bg-[#101113] border border-[#C17F59]/30 p-5 mb-6 flex items-center gap-4" data-testid="tonight-active">
          {(active.gin || active.beverage) && (
            <BottleSilhouette
              category={(active.gin?.category) || ((active.beverage?.category === "gin" ? "classic" : "classic"))}
              size={50}
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="overline text-[#C17F59] mb-1">Currently on the public landing</p>
            <p className="font-hand text-2xl text-[#D89A74]">~ {active.bartender}</p>
            <p className="text-sm text-white/80 italic">"{active.handwritten_note || active.note}"</p>
            <p className="text-xs text-white/50 mt-1 truncate">
              {(active.beverage?.display_name || active.gin?.name || active.flight?.name) || ""}
              {active.beverage ? ` · ${active.beverage.category}` : active.gin ? ` · ${active.gin.country}` : ""}
              {active.why_tonight ? ` · ${active.why_tonight}` : ""}
            </p>
            {active.expires_at && <p className="text-[10px] text-white/40 mt-1">Expires {new Date(active.expires_at).toLocaleString()}</p>}
          </div>
          <button onClick={clearTonight} className="text-xs uppercase tracking-[0.22em] text-white/50 hover:text-red-400" data-testid="tonight-clear">
            Clear
          </button>
        </div>
      )}

      {/* Target selector */}
      <div className="mb-5 flex gap-2" data-testid="tonight-target-selector">
        {[
          { key: "gin", label: "Gin" },
          { key: "beverage", label: "Other spirit" },
          { key: "flight", label: "Flight" },
        ].map((t) => (
          <button key={t.key}
                  onClick={() => setActiveTarget(t.key)}
                  data-testid={`tonight-target-${t.key}`}
                  className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                    activeTarget === t.key
                      ? "bg-[#C17F59] text-[#0E0F0C] border-[#C17F59]"
                      : "border-white/15 text-white/65 hover:border-white/30"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-[#15171A] border border-white/8 p-6 space-y-5" data-testid="tonight-form">
        <Row label="Bartender">
          <input value={form.bartender} onChange={(e) => setForm({ ...form, bartender: e.target.value })}
                 placeholder="e.g. Shrinkhala"
                 className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                 data-testid="tonight-bartender" />
        </Row>

        {activeTarget === "gin" && (
          <Row label="Featured gin">
            <select value={form.gin_id} onChange={(e) => setForm({ ...form, gin_id: e.target.value })}
                    className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="tonight-gin">
              <option value="" className="bg-[#15171A] text-white">— pick a gin —</option>
              {availableGins.map((g) => (
                <option key={g.id} value={g.id} className="bg-[#15171A] text-white">{`${g.name} · ${g.country}`}</option>
              ))}
            </select>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
              {availableGins.length} of {gins.length} gins are AVAILABLE.
            </p>
          </Row>
        )}

        {activeTarget === "beverage" && (
          <Row label="Featured spirit / non-gin SKU">
            <select value={form.beverage_id} onChange={(e) => setForm({ ...form, beverage_id: e.target.value })}
                    className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="tonight-beverage">
              <option value="" className="bg-[#15171A] text-white">— pick a bottle —</option>
              {availableBeverages.map((b) => (
                <option key={b.id} value={b.id} className="bg-[#15171A] text-white">{`${b.display_name} · ${b.category}${b.country ? ` · ${b.country}` : ""}`}</option>
              ))}
            </select>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
              {availableBeverages.length} AVAILABLE bottles across {new Set(availableBeverages.map((b) => b.category)).size} categories. Upload a menu under Menu Upload to grow this list.
            </p>
          </Row>
        )}

        {activeTarget === "flight" && (
          <Row label="Featured flight">
            <select value={form.flight_id} onChange={(e) => setForm({ ...form, flight_id: e.target.value })}
                    className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="tonight-flight">
              <option value="" className="bg-[#15171A] text-white">— pick a flight —</option>
              {flights.filter((f) => f.pourable !== false).map((f) => (
                <option key={f.id} value={f.id} className="bg-[#15171A] text-white">{f.name}</option>
              ))}
            </select>
          </Row>
        )}

        <Row label="Handwritten quote (the BIG italic line)">
          <textarea rows={3}
                    value={form.handwritten_note}
                    onChange={(e) => setForm({ ...form, handwritten_note: e.target.value })}
                    placeholder='e.g. "Warm, dense, dangerous. Goa rain deserves whisky like this."'
                    className="w-full bg-transparent border border-white/10 focus:border-[#C17F59] p-2 text-sm focus:outline-none resize-y font-serif italic"
                    data-testid="tonight-handwritten" />
          <p className="text-[10px] text-white/40 mt-1">This appears as the big handwritten line on the public homepage. Keep it short and sensory.</p>
        </Row>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Row label="Why tonight? (optional context)">
            <select value={form.why_tonight} onChange={(e) => setForm({ ...form, why_tonight: e.target.value })}
                    className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="tonight-why">
              {WHY_OPTIONS.map((w) => (
                <option key={w} value={w} className="bg-[#15171A] text-white">{w || "— skip —"}</option>
              ))}
            </select>
            <input value={form.why_tonight} onChange={(e) => setForm({ ...form, why_tonight: e.target.value })}
                   placeholder="…or type custom"
                   className="w-full bg-transparent text-white/75 border-b border-white/10 mt-2 py-1 text-xs focus:border-[#C17F59] focus:outline-none italic" />
          </Row>
          <Row label="Mood (optional, internal)">
            <select value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })}
                    className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="tonight-mood">
              {MOOD_OPTIONS.map((m) => (
                <option key={m} value={m} className="bg-[#15171A] text-white">{m || "— skip —"}</option>
              ))}
            </select>
          </Row>
        </div>

        <Row label="Expiry (optional — auto-clear at this time)">
          <input type="datetime-local"
                 value={form.expires_at}
                 onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                 className="bg-transparent border border-white/15 focus:border-[#C17F59] py-2 px-3 text-sm focus:outline-none w-full md:w-auto"
                 data-testid="tonight-expires" />
          <p className="text-[10px] text-white/40 mt-1">Leave empty to keep showing until you clear it.</p>
        </Row>

        <details>
          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white/70">Legacy: short "note" field (only shown on gin atlas Home — used by StaffPickStrip fallback)</summary>
          <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Older summary line — leave empty if Handwritten quote is set"
                    className="w-full bg-transparent border border-white/10 focus:border-[#C17F59] p-2 text-sm focus:outline-none resize-y mt-2"
                    data-testid="tonight-note" />
        </details>

        <button onClick={save} disabled={busy}
                className="bg-[#C17F59] text-[#0E0F0C] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                data-testid="tonight-save">
          {busy ? "Saving..." : "Push to Liquid Atlas"}
        </button>
        {savedAt && <p className="text-[10px] uppercase tracking-[0.25em] text-green-400 mt-2" data-testid="tonight-saved-stamp">Saved · {savedAt}</p>}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
